import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { BingoBoard } from "../../components/bingo/BingoBoard";
import { Scoreboard } from "../../components/bingo/Scoreboard";
import { WordStats } from "../../components/bingo/WordStats";
import {
  ActivityFeed,
  ActivityItem,
} from "../../components/bingo/ActivityFeed";
import { NamePrompt } from "../../components/bingo/NamePrompt";
import { CopyDialog } from "../../components/bingo/CopyDialog";
import { PlayerCardModal } from "../../components/bingo/PlayerCardModal";
import { RemovePlayerDialog } from "../../components/bingo/RemovePlayerDialog";
import { ObsSettings } from "../../components/bingo/ObsSettings";
import { wsService, checkAllBingos } from "../../services/websocket";
import {
  BingoCard,
  BingoCell,
  Player,
  ScoreboardUpdatePayload,
  WordStats as WordStatsType,
  WordStatsUpdatePayload,
  PlayerBannedPayload,
  PlayerKickedPayload,
} from "../../types";
import {
  getStoredPlayerName,
  setStoredPlayerName,
  getClientId,
  getBrowserFingerprint,
  getStoredPlayerId,
  setStoredPlayerId,
  setCurrentObsSession,
} from "../../utils/storage";
import "./PlayBingoView.css";
interface LocationState {
  card?: BingoCard;
  isHost?: boolean;
  playerId?: string;
  initialPlayers?: Player[];
  hostId?: string;
}

export const PlayBingoView: React.FC = () => {
  const { code: lobbyCode } = useParams<{ code: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as LocationState | null;
  const clientId = getClientId();

  const [card, setCard] = useState<BingoCard | null>(
    locationState?.card || null,
  );
  // Try to get playerId from locationState, otherwise from localStorage for this lobby
  const [playerId, setPlayerId] = useState<string | null>(() => {
    if (locationState?.playerId) return locationState.playerId;
    if (lobbyCode) {
      const stored = getStoredPlayerId(lobbyCode);
      if (stored) return stored;
    }
    return null;
  });
  const [hostId, setHostId] = useState<string | null>(
    locationState?.hostId || null,
  );
  const [playerName, setPlayerName] = useState<string | null>(null);
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [isLoading, setIsLoading] = useState(!locationState?.card);
  const [error, setError] = useState<string | null>(null);
  const [hasBingo, setHasBingo] = useState(false);
  const [bingoCount, setBingoCount] = useState(0);
  const [prevBingoCount, setPrevBingoCount] = useState(0);
  const [winningIndices, setWinningIndices] = useState<number[] | null>(null);
  const [players, setPlayers] = useState<Player[]>(
    locationState?.initialPlayers || [],
  );
  const playersRef = React.useRef<Player[]>(
    locationState?.initialPlayers || [],
  );
  const [wordStats, setWordStats] = useState<WordStatsType[]>([]);
  const [copyDialog, setCopyDialog] = useState<{
    title: string;
    value: string;
  } | null>(null);
  const [initialBingoChecked, setInitialBingoChecked] = useState(false);
  const [hasJoinedLobby, setHasJoinedLobby] = useState(!!locationState?.card);
  const [activeTab, setActiveTab] = useState<
    "selected" | "players" | "activity" | "obs"
  >("selected");
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [viewingPlayerCard, setViewingPlayerCard] = useState<{
    playerName: string;
    gridSize: number;
    cells: BingoCell[];
  } | null>(null);
  const [removePlayerDialog, setRemovePlayerDialog] = useState<{
    playerId: string;
    playerName: string;
  } | null>(null);

  // keep latest players for activity feed lookups
  useEffect(() => {
    playersRef.current = players;
  }, [players]);

  // Load persisted activities per lobby on mount
  useEffect(() => {
    if (!lobbyCode) return;
    try {
      const raw = localStorage.getItem(`bingo_activities_${lobbyCode}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setActivities(parsed);
        }
      }
    } catch (err) {
      console.warn("Failed to load stored activities", err);
    }
  }, [lobbyCode]);

  // Persist activities so they are available even if the tab wasn't open
  useEffect(() => {
    if (!lobbyCode) return;
    try {
      localStorage.setItem(
        `bingo_activities_${lobbyCode}`,
        JSON.stringify(activities.slice(0, 100)),
      );
    } catch (err) {
      console.warn("Failed to store activities", err);
    }
  }, [activities, lobbyCode]);

  // Check for stored name on mount or prompt for name
  // On mount: check for stored name, otherwise show modal dialog
  useEffect(() => {
    const storedName = getStoredPlayerName();
    if (storedName) {
      setPlayerName(storedName);
    } else {
      setShowNamePrompt(true);
      setIsLoading(false); // Stop loading while waiting for name
    }
  }, []);

  // Listen to scoreboard updates, word stats updates, and ban notifications
  useEffect(() => {
    const unsubscribe = wsService.subscribe((message) => {
      if (message.type === "scoreboard_update") {
        const payload = message.payload as ScoreboardUpdatePayload;
        setPlayers(payload.players);
        if (payload.hostId) {
          setHostId(payload.hostId);
        }
      } else if (message.type === "word_stats_update") {
        const payload = message.payload as WordStatsUpdatePayload;
        setWordStats(payload.wordStats);
      } else if (message.type === "word_clicked") {
        // Track activity for word clicks from all players
        const payload = message.payload as any;
        const player = playersRef.current.find(
          (p) => p.id === payload.playerId,
        );
        // Nur eine Aktivität pro Spieler+Wort+Status anzeigen
        const uniqueKey = `word-${payload.playerId}-${payload.word}-${payload.marked}`;
        setActivities((prev) => {
          // Entferne alte Aktivitäten mit gleichem Spieler+Wort+Status
          const filtered = prev.filter(
            (a) =>
              !(
                a.type === "word_click" &&
                a.playerId === payload.playerId &&
                a.word === payload.word &&
                a.marked === payload.marked
              ),
          );
          const newActivity: ActivityItem = {
            id: uniqueKey + "-" + Date.now(),
            type: "word_click",
            timestamp: Date.now(),
            playerName: player?.name || payload.playerId,
            playerId: payload.playerId,
            word: payload.word,
            marked: payload.marked,
          };
          return [newActivity, ...filtered].slice(0, 100);
        });
      } else if (message.type === "bingo_updated") {
        // Track activity for bingo achievements from all players
        const payload = message.payload as any;
        const player = playersRef.current.find(
          (p) => p.id === payload.playerId,
        );
        if (payload.bingoCount > 0) {
          // Create unique ID based on event details and timestamp to prevent duplicates
          const uniqueId = `bingo-${payload.playerId}-${
            payload.bingoCount
          }-${Date.now()}`;
          setActivities((prev) => {
            const newActivity: ActivityItem = {
              id: uniqueId,
              type: "bingo",
              timestamp: Date.now(),
              playerName: player?.name || payload.playerId,
              playerId: payload.playerId,
              bingoCount: payload.bingoCount,
            };
            return [newActivity, ...prev].slice(0, 100); // Keep last 100
          });
        }
      } else if (message.type === "player_banned") {
        const payload = message.payload as PlayerBannedPayload;
        if (payload.playerId === playerId) {
          // Current player was banned - redirect to message page
          navigate("/message", {
            state: {
              title: "Gebannt! 🚫",
              message: "Du wurdest dauerhaft aus der Lobby verbannt.",
              type: "error",
            },
          });
        }
      } else if (message.type === "player_kicked") {
        const payload = message.payload as PlayerKickedPayload;
        if (payload.playerId === playerId) {
          // Current player was kicked - redirect to message page
          navigate("/message", {
            state: {
              title: "Gekickt! 🦶",
              message:
                "Du wurdest aus der Lobby entfernt. Du kannst aber wieder joinen.",
              type: "warning",
            },
          });
        }
      }
    });

    return unsubscribe;
  }, [playerId, navigate]);

  // Join lobby after name is set
  useEffect(() => {
    if (hasJoinedLobby || !lobbyCode || !playerName) return;

    let isCancelled = false;

    const joinLobby = async () => {
      setIsLoading(true);
      setError(null);

      try {
        await wsService.connect();
        if (isCancelled) return;

        const fingerprint = await getBrowserFingerprint();
        if (isCancelled) return;

        const response = await wsService.joinLobby({
          lobbyCode,
          playerName,
          clientId,
          fingerprint,
        });

        if (isCancelled) return;

        setCard(response.card);
        setPlayerId(response.playerId);
        setPlayers(response.players || []);
        setHostId(response.hostId || null);
        if (response.wordStats) {
          setWordStats(response.wordStats);
        }
        setHasJoinedLobby(true);
        // Store playerId for this lobby (for reconnecting)
        setStoredPlayerId(lobbyCode, response.playerId);
        // Update current OBS session so OBS widget can auto-switch to new game
        setCurrentObsSession(lobbyCode, response.playerId);
      } catch (err) {
        if (isCancelled) return;

        console.error("Failed to join lobby:", err);
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Lobby konnte nicht betreten werden. Bitte versuche es erneut.";
        // Check for ban message and redirect to message page
        if (
          errorMessage.toLowerCase().includes("banned") ||
          errorMessage.toLowerCase().includes("ban")
        ) {
          navigate("/message", {
            state: {
              title: "Zugang verweigert 🚫",
              message: "Du wurdest aus dieser Lobby verbannt.",
              type: "error",
            },
          });
          return;
        }
        // Check for lobby not found
        if (errorMessage.toLowerCase().includes("not found")) {
          navigate("/message", {
            state: {
              title: "Lobby nicht gefunden 🔍",
              message: "Diese Lobby existiert nicht oder wurde geschlossen.",
              type: "warning",
            },
          });
          return;
        }
        setError(errorMessage);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };
    joinLobby();

    return () => {
      isCancelled = true;
    };
  }, [lobbyCode, hasJoinedLobby, playerName, clientId, navigate]);

  // Check for existing bingo on card load (without showing popup)
  // This handles the case when page is reloaded and player already had bingo
  useEffect(() => {
    if (!card || initialBingoChecked) return;

    // Mark that we've done the initial check
    setInitialBingoChecked(true);

    const allBingos = checkAllBingos(card.cells, card.gridSize);
    const currentBingoCount = allBingos.length;

    if (currentBingoCount > 0) {
      const allWinningIndices = allBingos.flat();
      const uniqueWinningIndices = Array.from(new Set(allWinningIndices));

      setWinningIndices(uniqueWinningIndices);
      setBingoCount(currentBingoCount);
      // Set prevBingoCount to current so popup doesn't trigger on next click
      setPrevBingoCount(currentBingoCount);
      // Don't set hasBingo to true - no popup on reload
    }
  }, [card, initialBingoChecked]);

  // Handle cell toggle (local marking only)
  const handleCellToggle = useCallback(
    (cellId: string) => {
      if (!card) return;

      // Find the cell being toggled
      const toggledCell = card.cells.find((c) => c.id === cellId);
      if (!toggledCell) return;

      const newMarkedState = !toggledCell.marked;

      // Send word click event to server (ONCE, outside setState)
      if (lobbyCode && playerId) {
        wsService.sendWordClick({
          lobbyCode,
          playerId,
          word: toggledCell.text,
          marked: newMarkedState,
        });
      }

      setCard((prevCard) => {
        if (!prevCard) return null;

        const updatedCells = prevCard.cells.map((cell) =>
          cell.id === cellId ? { ...cell, marked: !cell.marked } : cell,
        );

        // Check for all bingos
        const allBingos = checkAllBingos(updatedCells, prevCard.gridSize);
        const currentBingoCount = allBingos.length;

        // Flatten all winning indices into one set
        const allWinningIndices = allBingos.flat();
        const uniqueWinningIndices = Array.from(new Set(allWinningIndices));

        setBingoCount(currentBingoCount);

        if (currentBingoCount > 0) {
          setWinningIndices(uniqueWinningIndices);
          // Show alert if we have a new bingo (count increased)
          if (currentBingoCount > prevBingoCount) {
            setHasBingo(true);
            setPrevBingoCount(currentBingoCount);

            // Send bingo update to server
            if (lobbyCode && playerId) {
              wsService.updateBingo({
                lobbyCode,
                playerId,
                bingoCount: currentBingoCount,
              });
            }
          }
        } else {
          setWinningIndices(null);
          setHasBingo(false);
          setPrevBingoCount(0);

          // Send bingo update to server (reset to 0)
          if (lobbyCode && playerId) {
            wsService.updateBingo({
              lobbyCode,
              playerId,
              bingoCount: 0,
            });
          }
        }

        return {
          ...prevCard,
          cells: updatedCells,
        };
      });
    },
    [card, prevBingoCount, lobbyCode, playerId],
  );

  const handleNameSubmit = (name: string) => {
    setPlayerName(name);
    setStoredPlayerName(name);
    setShowNamePrompt(false);
  };

  const handleNameClose = () => {
    setShowNamePrompt(false);
  };

  // Handle banning a player (only host can do this)
  const handleBanPlayer = useCallback(
    (targetPlayerId: string) => {
      if (!lobbyCode || !playerId || !hostId || playerId !== hostId) return;

      wsService.banPlayer({
        lobbyCode,
        hostId: playerId,
        targetPlayerId,
      });
      setRemovePlayerDialog(null);
    },
    [lobbyCode, playerId, hostId],
  );

  // Handle kicking a player (only host can do this)
  const handleKickPlayer = useCallback(
    (targetPlayerId: string) => {
      if (!lobbyCode || !playerId || !hostId || playerId !== hostId) return;

      wsService.kickPlayer({
        lobbyCode,
        hostId: playerId,
        targetPlayerId,
      });
      setRemovePlayerDialog(null);
    },
    [lobbyCode, playerId, hostId],
  );

  // Show remove player dialog
  const handleRemovePlayer = useCallback(
    (targetPlayerId: string, targetPlayerName: string) => {
      setRemovePlayerDialog({
        playerId: targetPlayerId,
        playerName: targetPlayerName,
      });
    },
    [],
  );

  // Handle going back to create view
  const handleBackToCreate = () => {
    navigate("/");
  };

  // Copy lobby link to clipboard
  const handleCopyLink = () => {
    if (!lobbyCode) return;
    const url = `${window.location.origin}/play/${lobbyCode}`;
    setCopyDialog({ title: "Lobby-Link teilen", value: url });
  };

  // Copy OBS link to clipboard
  const handleCopyObsLink = () => {
    if (!lobbyCode || !playerId) return;
    const url = `${window.location.origin}/obs/${lobbyCode}/${playerId}`;
    setCopyDialog({ title: "OBS Browser-Quelle Link", value: url });
  };

  // View another player's card
  const handleViewCard = useCallback(
    async (targetPlayerId: string, targetPlayerName: string) => {
      if (!lobbyCode) return;

      try {
        const response = await wsService.getPlayerCard({
          lobbyCode,
          playerId: targetPlayerId,
        });

        setViewingPlayerCard({
          playerName: targetPlayerName,
          gridSize: response.gridSize,
          cells: response.card.cells,
        });
      } catch (error) {
        console.error("Failed to get player card:", error);
      }
    },
    [lobbyCode],
  );

  // No lobby code
  if (!lobbyCode) {
    return (
      <div className="play-bingo-view">
        <div className="play-bingo-view__message">
          <h2>Kein Lobby-Code</h2>
          <p>
            Bitte tritt einer Lobby mit einem gültigen Link bei oder erstelle
            eine neue.
          </p>
          <button onClick={handleBackToCreate} className="play-bingo-view__btn">
            Neues Bingo erstellen
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="play-bingo-view">
        <div className="play-bingo-view__message">
          <div className="play-bingo-view__spinner"></div>
          <p>Trete Lobby bei...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="play-bingo-view">
        <div className="play-bingo-view__message">
          <h2>Fehler</h2>
          <p>{error}</p>
          <button onClick={handleBackToCreate} className="play-bingo-view__btn">
            Neues Bingo erstellen
          </button>
        </div>
      </div>
    );
  }

  // No card, but if name is missing, show only the name prompt dialog (modal style)
  if (!card && showNamePrompt) {
    return (
      <div className="play-bingo-view">
        <NamePrompt onSubmit={handleNameSubmit} onClose={handleNameClose} />
      </div>
    );
  }
  if (!card) {
    return (
      <div className="play-bingo-view">
        <div className="play-bingo-view__message">
          <h2>Keine Karte gefunden</h2>
          <p>Deine Bingo-Karte konnte nicht geladen werden.</p>
          <button onClick={handleBackToCreate} className="play-bingo-view__btn">
            Neues Bingo erstellen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="play-bingo-view">
      {showNamePrompt && (
        <NamePrompt onSubmit={handleNameSubmit} onClose={handleNameClose} />
      )}
      {copyDialog && (
        <CopyDialog
          title={copyDialog.title}
          value={copyDialog.value}
          onClose={() => setCopyDialog(null)}
        />
      )}
      {viewingPlayerCard && (
        <PlayerCardModal
          playerName={viewingPlayerCard.playerName}
          gridSize={viewingPlayerCard.gridSize}
          cells={viewingPlayerCard.cells}
          onClose={() => setViewingPlayerCard(null)}
        />
      )}

      {removePlayerDialog && (
        <RemovePlayerDialog
          playerName={removePlayerDialog.playerName}
          onKick={() => handleKickPlayer(removePlayerDialog.playerId)}
          onBan={() => handleBanPlayer(removePlayerDialog.playerId)}
          onCancel={() => setRemovePlayerDialog(null)}
        />
      )}

      <header className="play-bingo-view__header">
        <h1>BINGO</h1>
        <div className="play-bingo-view__lobby-info">
          {
            //<span className="play-bingo-view__lobby-code">{lobbyCode}</span>
          }
          <button
            onClick={handleCopyLink}
            className="play-bingo-view__copy-btn"
            title="Einladungslink kopieren"
          >
            Link kopieren
          </button>
          <button
            onClick={handleCopyObsLink}
            className="play-bingo-view__copy-btn play-bingo-view__copy-btn--obs"
            title="OBS Browser-Quelle Link kopieren"
          >
            OBS Link
          </button>
          <button
            onClick={handleBackToCreate}
            className="play-bingo-view__copy-btn play-bingo-view__copy-btn--new-game"
            title="Neues Bingo erstellen"
          >
            Neues Spiel
          </button>
        </div>
      </header>

      {hasBingo && (
        <div className="play-bingo-view__bingo-overlay">
          <div className="play-bingo-view__bingo-alert">
            <div className="play-bingo-view__bingo-stars">✨</div>
            <span className="play-bingo-view__bingo-text">
              {bingoCount === 1 ? "BINGO!" : `${bingoCount}x BINGO!`}
            </span>
            <div className="play-bingo-view__bingo-stars">🎉</div>
            <button
              className="play-bingo-view__bingo-continue"
              onClick={() => setHasBingo(false)}
            >
              Weiterspielen
            </button>
          </div>
        </div>
      )}

      <div className="play-bingo-view__content">
        <div className="play-bingo-view__sidebar play-bingo-view__sidebar--left">
          <div className="play-bingo-view__tabs">
            <button
              className={`play-bingo-view__tab ${
                activeTab === "selected" ? "play-bingo-view__tab--active" : ""
              }`}
              onClick={() => setActiveTab("selected")}
            >
              Ausgewählt
            </button>
            <button
              className={`play-bingo-view__tab ${
                activeTab === "players" ? "play-bingo-view__tab--active" : ""
              }`}
              onClick={() => setActiveTab("players")}
            >
              Spieler ({players.length})
            </button>
            <button
              className={`play-bingo-view__tab ${
                activeTab === "activity" ? "play-bingo-view__tab--active" : ""
              }`}
              onClick={() => setActiveTab("activity")}
            >
              Aktivität
            </button>
            <button
              className={`play-bingo-view__tab ${
                activeTab === "obs" ? "play-bingo-view__tab--active" : ""
              }`}
              onClick={() => setActiveTab("obs")}
            >
              OBS
            </button>
          </div>
          <div className="play-bingo-view__tab-content">
            {activeTab === "selected" && <WordStats wordStats={wordStats} />}
            {activeTab === "players" && (
              <Scoreboard
                players={players}
                currentPlayerId={playerId || undefined}
                hostId={hostId || undefined}
                onRemovePlayer={handleRemovePlayer}
                onViewCard={handleViewCard}
              />
            )}
            {activeTab === "activity" && (
              <ActivityFeed activities={activities} />
            )}
            {activeTab === "obs" && lobbyCode && (
              <ObsSettings lobbyCode={lobbyCode} isHost={playerId === hostId} />
            )}
          </div>
        </div>

        <div className="play-bingo-view__board-container">
          <BingoBoard
            gridSize={card.gridSize}
            cells={card.cells}
            winningIndices={winningIndices}
            wordStats={wordStats}
            onCellToggle={handleCellToggle}
          />
        </div>
      </div>
    </div>
  );
};
